<template>
  <ConfirmDialog
      v-model="dialog"
      @update:agree="confirmAction"
      @update:cancel="closeModal"
      ref="confirm"
      v-if="selectedItem"
  >
    <template v-slot:title>
      {{ selectedItem.name }}
    </template>
    <template v-slot:content>
      <v-card-text>
        <v-form ref="form">
          <div v-for="schema in schemas" class="mb-2">
            <div class="description mb-2" v-if="schema.descriptionSideUp">
              {{schema.description}}
            </div>
            <div> <!-- деление по типам -->
              <v-text-field
                  v-if="schema.type === 'String'"
                  :name="schema.key"
                  :label="schema.label"
                  :placeholder="schema.placeholder"
                  v-model:model-value="values[schema.key]"
              />
              <v-checkbox
                  v-if="schema.type === 'Boolean'"
                  class="specCheckbox"
                  :name="schema.key"
                  :label="schema.label"
                  :placeholder="schema.placeholder"
                  :true-icon="'mdi-check mdi-18px'"
                  :false-icon="'voidCheckbox'"
                  v-model:model-value="values[schema.key]"
              />
              <CountInput
                  v-if="schema.type === 'Number'"
                  :name="schema.key"
                  :label="schema.label"
                  :placeholder="schema.placeholder"
                  :precision="{min: 0, max: 2}"
                  v-model="values[schema.key]"
                  :rules="[
                    ...validation.number(0.01, schema.maxVal ?? 999_999.99, true, true, true),
                    ...(schema.key === 'min' ? validation.minValue(values.max) : []),
                    ...(schema.key === 'max' ? validation.maxValue(values.min) : [])
                  ]"
                  :max="schema.maxVal"
                />
                <FileInput
                  v-if="schema.type === 'Document'"
                  no-card without-title without-sub-title rewriteModal
                  is-edit
                  :maxCountFiles="1"
                  v-model:documents="documents[schema.key]"
                  :upload-file="(file, rewrite) => onUpload(schema.key, file, schema.uploadExtension, rewrite)"
                  :delete-file="file => onDocDelete(schema.key, file)"
                  :download-file="id => downloadDocument(schema.key, id)"
                  :uploadExtensions="schema.uploadExtension"
                  :format-file-info="[
                    (file) => formatMinuteTimeNoTZ(file.createdAt),
                    (file) => file.fileSizeFmt.toLowerCase()
                  ]"
                >
                  <template v-slot:file-icon="{ document }">
                    <FileIcon :document="document" />
                  </template>
                </FileInput>
              </div>
              <div class="description" v-if="!schema.descriptionSideUp">
                {{ schema.description }}
              </div>
            </div>
          </v-form>
        </v-card-text>
    </template>
    <template v-slot:cancelText>{{ $t('Отменить') }}</template>
    <template v-slot:agreeText>{{ $t('Сохранить') }}</template>
  </ConfirmDialog>
</template>

<script>
import CountInput from "@/components/form/CountInput.vue";
import validation from "@/components/validation";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import api from "@/api";
import { toast } from '@/lib/toast';
import FileInput from "@intec/vue-components/form/FileInput.vue";
import { formatByteSize, formatMinuteTimeNoTZ } from "@/filters";
import FileIcon from "@/components/FileIcon.vue";

export default {
  name: 'ConfigsModal',
  components: { FileIcon, FileInput, CountInput, ConfirmDialog },
  props: {
    selectedItem: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      dialog: false,
      values: {},
      loading: false,
      files: {},
      documents: {},
      documentKey: 1
    }
  },
  emits: ['close'],
  watch: {
    selectedItem(val) {
      this.dialog = val !== null;
      if (this.dialog) {
        for (let i = 0; i < this.schemas.length; i++) {
          const curSch = this.schemas[i];
          this.values[curSch.key] = this.selectedItem.value[curSch.key];
        }
      } else {
        this.values = {};
      }
    }
  },
  computed: {
    validation() {
      return validation
    },
    schemas() {
      const schemaArr = Array.isArray(this.selectedItem?.schema) ? this.selectedItem?.schema : [this.selectedItem?.schema];
      schemaArr.filter(schema => schema.type === 'Document').forEach(schema => {
        const doc = this.selectedItem.value[`doc_${schema.key}`];
        this.documents[schema.key] = doc?.id > 0 ? [doc] : [];
        this.files[schema.key] = doc?.id > 0 ? [doc] : [];
      });
      return schemaArr;
    }
  },
  methods: {
    formatMinuteTimeNoTZ,
    closeModal(params) {
      this.$emit('close', params);
    },
    async confirmAction() {
      try {
        if ((await this.$refs.form.validate()).valid) {
          this.loading = true;
          const formData = new FormData();
          Object.keys(this.files).forEach(key => {
            if (this.documents[key][0]?.id) {
              if (this.documents[key][0].id !== this.values[key]) {
                formData.append(key, this.files[key][0]);
                this.values[`doc_${key}`] = {...this.documents[key][0]};
              }
            } else {
              this.values[`doc_${key}`] = null;
            }
          });
          formData.append('data', JSON.stringify({
            value: this.values,
            code: this.selectedItem.code
          }));
          await api.post('/admin/configs/edit', formData);
          toast.success('Данные проверены и сохранены')
          this.closeModal({reload: true});
        } else {
          throw new Error(this.$t(`Ошибка: Данные некорректны`));
        }
      } catch (e) {
        toast.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    onUpload(key, file, uploadExtension, rewrite) {
      const filename = file.name;
      const fileExtension = filename.slice(filename.lastIndexOf("."));
      if (fileExtension !== uploadExtension) {
        toast.error(this.$t(`Принимаются файлы только с расширением {_1}`, {'_1': uploadExtension}));
      } else {
        this.files[key] = rewrite ? [] : [...this.files[key]];
        this.documents[key] = rewrite ? [] : [...this.documents[key]];
        this.files[key].push(file);
        this.documents[key].push({
          ...file,
          createdAt: new Date(),
          fileName: file.name,
          fileSizeFmt: formatByteSize(file.size),
          id: this.getNextKey()
        });
      }
    },
    getNextKey() {
      return this.documentKey++;
    },
    onDocDelete(key, item) {
      this.files[key].splice(this.files[key].findIndex(file => file.fileName === item.fileName), 1);
      this.documents[key].splice(this.documents[key].findIndex(doc => doc.fileName === item.fileName), 1);
    },
    downloadDocument(key, documentId) {
      if (this.documents[key][0] === this.files[key][0]) {
        window.location.href = '/admin/configs/file/' + this.values[key];
      } else {
        const findedDoc = this.documents[key].find(doc => doc.id === documentId);
        const findedFile = this.files[key].find(file => file.name === findedDoc.fileName)
        const link = this.$refs.loadButton;
        link.href = URL.createObjectURL(findedFile);
        link.download = findedFile.name;
        link.click();
      }
    }
  }
};
</script>

<style scoped>

:deep(.mdi-check.mdi-18px) {
  border-radius: 3px;
  border: 1px solid rgb(var(--black-color));
}

:deep(.voidCheckbox) {
  border-radius: 3px;
  border: 1px solid rgb(var(--black-color));
  background-color: rgb(var(--white-color));
}

:deep(.specCheckbox .v-label) {
  color: rgb(var(--black-color));
  font-family: Abrade;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  opacity: 1;
}

.description {
  color: rgb(var(--gunsmoke-color));
}

:deep(.v-checkbox .v-label) {
  font-size: 16px;
}
</style>